import type { Moment } from 'moment';
import moment from 'moment';

import ExceptionService from '@src/services/ExceptionService';

import type { CoverReplaceModificationForm, TCoverReplaceDurationMode } from '../types';

export type CoverReplaceAllowedRange = {
  from: string;
  to: string;
};

export type CoverReplaceExceptionLike = {
  id?: string;
  type?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

const parseIso = (value?: string | null): Moment | null => {
  if (!value) return null;
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed : null;
};

const toIso = (value: Moment): string => value.format('YYYY-MM-DD');

/**
 * Builds the selectable replacement window from an absence exception and/or
 * the attention-row cover dates. Exception bounds win when present.
 */
export const resolveCoverReplaceAllowedRange = (params: {
  coverDate?: string | null;
  coverDateTo?: string | null;
  exceptionStartDate?: string | null;
  exceptionEndDate?: string | null;
}): CoverReplaceAllowedRange | null => {
  const exceptionFrom = parseIso(params.exceptionStartDate);
  const exceptionTo = parseIso(params.exceptionEndDate);
  if (exceptionFrom && exceptionTo) {
    const from = exceptionFrom.isAfter(exceptionTo, 'day') ? exceptionTo : exceptionFrom;
    const to = exceptionFrom.isAfter(exceptionTo, 'day') ? exceptionFrom : exceptionTo;
    return { from: toIso(from), to: toIso(to) };
  }

  const coverFrom =
    parseIso(params.coverDate) ?? parseIso(params.coverDateTo) ?? null;
  if (!coverFrom) return null;

  const coverTo = parseIso(params.coverDateTo) ?? coverFrom;
  const from = coverFrom.isAfter(coverTo, 'day') ? coverTo : coverFrom;
  const to = coverFrom.isAfter(coverTo, 'day') ? coverFrom : coverTo;
  return { from: toIso(from), to: toIso(to) };
};

/**
 * Picks the absence exception that covers `coverDate` for the replaced user.
 * Skips shift_replacement rows (those are covers, not the original leave).
 */
export const pickAbsenceExceptionForCoverDate = (
  exceptions: CoverReplaceExceptionLike[] | null | undefined,
  coverDate?: string | null,
  preferredExceptionId?: string | null,
): CoverReplaceExceptionLike | null => {
  const cover = parseIso(coverDate);
  const list = (exceptions ?? []).filter(exception => {
    if (!exception) return false;
    if (exception.type === ExceptionService.TYPES.SHIFT_REPLACEMENT) return false;
    const start = parseIso(exception.startDate);
    const end = parseIso(exception.endDate);
    return Boolean(start && end);
  });

  if (list.length === 0) return null;

  if (preferredExceptionId) {
    const preferred = list.find(exception => exception.id === preferredExceptionId);
    if (preferred) return preferred;
  }

  if (!cover) return list[0] ?? null;

  const covering = list.filter(exception => {
    const start = parseIso(exception.startDate)!;
    const end = parseIso(exception.endDate)!;
    return !cover.isBefore(start, 'day') && !cover.isAfter(end, 'day');
  });

  if (covering.length === 0) return null;

  // Prefer the *shortest* covering window (e.g. 2-day VAC over a wider leave
  // that also overlaps the attention day). Longest was expanding REM ranges.
  return covering.reduce((best, current) => {
    const bestStart = parseIso(best.startDate)!;
    const bestEnd = parseIso(best.endDate)!;
    const currentStart = parseIso(current.startDate)!;
    const currentEnd = parseIso(current.endDate)!;
    const bestSpan = bestEnd.diff(bestStart, 'days');
    const currentSpan = currentEnd.diff(currentStart, 'days');
    return currentSpan < bestSpan ? current : best;
  });
};

/** True when `value` is outside the inclusive allowed ISO range. */
export const isOutsideCoverReplaceAllowedRange = (
  value: Moment | null | undefined,
  allowedRange: CoverReplaceAllowedRange | null | undefined,
): boolean => {
  if (!value || !value.isValid() || !allowedRange) return false;
  const from = parseIso(allowedRange.from);
  const to = parseIso(allowedRange.to);
  if (!from || !to) return false;
  return value.isBefore(from, 'day') || value.isAfter(to, 'day');
};

/** Clamps a moment into the allowed range (inclusive). */
export const clampToCoverReplaceAllowedRange = (
  value: Moment | null,
  allowedRange: CoverReplaceAllowedRange | null | undefined,
): Moment | null => {
  if (!value || !value.isValid()) return value;
  if (!allowedRange) return value;
  const from = parseIso(allowedRange.from);
  const to = parseIso(allowedRange.to);
  if (!from || !to) return value;
  if (value.isBefore(from, 'day')) return from.clone();
  if (value.isAfter(to, 'day')) return to.clone();
  return value;
};

/**
 * Seeds the replacement period from the resolved absence / cover window.
 */
export const buildCoverReplaceModificationFormFromRange = (
  allowedRange: CoverReplaceAllowedRange | null,
  comment = '',
): CoverReplaceModificationForm => {
  const start = parseIso(allowedRange?.from) ?? moment();
  const end = parseIso(allowedRange?.to) ?? start.clone();
  const isSingleDay = end.isSame(start, 'day');

  return {
    durationMode: isSingleDay ? 'single' : 'range',
    startDate: start,
    endDate: end,
    comment,
  };
};

/**
 * Keeps the step-2 form inside the selectable window.
 * When bounds come from an absence exception (e.g. 2 VAC days), always reset
 * to that window — clamping a stale wider form left REM spanning a full week.
 */
export const syncCoverReplaceModificationFormToAllowedRange = (
  prev: CoverReplaceModificationForm,
  allowedRange: CoverReplaceAllowedRange,
  allowedRangeFromAbsence: boolean,
): CoverReplaceModificationForm => {
  if (allowedRangeFromAbsence) {
    return buildCoverReplaceModificationFormFromRange(allowedRange, prev.comment);
  }
  if (prev.durationMode === 'permanent') {
    return prev;
  }
  const startDate = clampToCoverReplaceAllowedRange(prev.startDate, allowedRange);
  const endDate =
    prev.durationMode === 'single'
      ? startDate
      : clampToCoverReplaceAllowedRange(prev.endDate, allowedRange);
  if (
    startDate?.isSame(prev.startDate, 'day') &&
    endDate?.isSame(prev.endDate, 'day')
  ) {
    return prev;
  }
  return { ...prev, startDate, endDate };
};

/**
 * Defense-in-depth before persisting a replacement.
 * Permanent mode uses a sentinel end date outside any cover window — skip it.
 */
export const isCoverReplaceSubmitOutsideAllowedRange = (params: {
  startDate: string;
  endDate: string;
  allowedRange: CoverReplaceAllowedRange | null | undefined;
  durationMode: TCoverReplaceDurationMode;
}): boolean => {
  const { startDate, endDate, allowedRange, durationMode } = params;
  if (!allowedRange || durationMode === 'permanent') {
    return false;
  }
  return (
    startDate < allowedRange.from ||
    endDate > allowedRange.to ||
    startDate > endDate
  );
};
